import { PageProps } from "@/types";
import { patientMaterialSections, patientTherapeuticAreas } from "@/constants/patientTherapeuticAreas";
import { Transition } from "@headlessui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, usePage } from "@inertiajs/react";
import { Fragment, useEffect, useState } from "react";

const PatientNavigationMenu = ({ state, setState }: {
    state: boolean,
    setState: (value: boolean) => void
}) => {
    const { user } = usePage<PageProps>().props.auth;
    const [showMaterials, setShowMaterials] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<(typeof patientTherapeuticAreas)[number] | null>(null);

    const openMaterialsMenu = (category: (typeof patientTherapeuticAreas)[number]) => {
        setSelectedCategory(category);
        setShowMaterials(true);
    };

    useEffect(() => {
        if (!state) {
            setShowMaterials(false);
            setSelectedCategory(null);
        }
    }, [state]);

    const closeMaterialsMenu = () => {
        setShowMaterials(false);
        setSelectedCategory(null);
    };

    const closeMenus = () => {
        closeMaterialsMenu();
        setState(false);
    };

    const menuLinkClass = "navigation-menu-link group hover:text-accordorange maxMd:w-full maxMd:justify-start maxMd:text-left maxMd:whitespace-normal maxMd:break-words";

    const MenuArrowIcon = ({ variant = 'forward' }: { variant?: 'forward' | 'back' }) => (
        <Icon
            icon={variant === 'forward' ? 'material-symbols:arrow-forward-rounded' : 'material-symbols:arrow-back-rounded'}
            width={24}
            className={
                variant === 'forward'
                    ? 'transition-theme shrink-0 text-arcticMist md:opacity-0 md:group-hover:opacity-100'
                    : 'transition-theme opacity-0 text-arcticMist group-hover:opacity-100 shrink-0'
            }
        />
    );

    return (
        <>
            <Transition
                as={Fragment}
                show={state}
                enter="transition-theme"
                enterFrom="opacity-0 translate-x-[50%]"
                enterTo="opacity-100 translate-x-[0%]"
                leave="transition-theme"
                leaveFrom="opacity-100 translate-x-[0%]"
                leaveTo="opacity-0 translate-x-[50%]"
            >
                <div className={`bg-midnightAbyss pr-14 pl-8 pb-8 absolute right-0 top-0 pt-6 xl:w-[25%] md:w-[45%] h-screen border-midnightNavy z-20 maxMd:flex maxMd:flex-col maxMd:min-h-screen maxMd:px-4 maxMd:pb-20 maxMd:w-full overflow-y-scroll ${showMaterials ? 'maxMd:hidden' : ''}`}>
                    <div className="w-full pb-6">
                        <button onClick={() => closeMenus()} type='button' className='ml-auto w-10 h-10 flex items-center justify-center p-2 bg-royalPeriwinkle rounded-full transition-theme hover:opacity-75 text-arcticMist text-14 leading-16 tracking-[0.21px]'>
                            <Icon icon={`ic:round-close`} width={24} className="text-arcticMist" />
                        </button>
                    </div>
                    <div className="w-full pt-28 flex flex-col items-end maxMd:items-start maxMd:pt-12">
                        <ul className="maxMd:w-full">
                            {patientTherapeuticAreas.map((area) => (
                                <li key={area.slug} className="my-4">
                                    <button
                                        type="button"
                                        title={area.label}
                                        className={menuLinkClass}
                                        onClick={() => openMaterialsMenu(area)}
                                    >
                                        <MenuArrowIcon />
                                        {area.label}
                                    </button>
                                </li>
                            ))}
                            {(user && (user.roles.some((role) => role.name === 'super-admin') || user.roles.some((role) => role.name === 'doctor'))) &&
                                <li className="my-4">
                                    <Link href={route('app')} title="Dla lekarza" className={menuLinkClass} onClick={() => closeMenus()}>
                                        <MenuArrowIcon />
                                        Dla lekarza
                                    </Link>
                                </li>
                            }
                        </ul>
                    </div>
                </div>
            </Transition>
            <Transition
                as={Fragment}
                show={showMaterials && !!selectedCategory}
                enter="transition-theme"
                enterFrom="opacity-0 translate-x-[50%]"
                enterTo="opacity-100 translate-x-[0%]"
                leave="transition-theme"
                leaveFrom="opacity-100 translate-x-[0%]"
                leaveTo="opacity-0 translate-x-[50%]"
            >
                <div className="bg-gradientPopup pr-14 pl-8 pb-8 absolute xl:right-[25%] lg:right-[35%] top-0 pt-6 xl:w-[25%] lg:w-[35%] md:w-[50%] md:right-[40%] h-screen z-[99999] maxMd:fixed maxMd:inset-0 maxMd:w-full maxMd:right-0 maxMd:left-0 maxMd:px-4 maxMd:pb-20 overflow-y-scroll">
                    <div className="w-full pb-6 flex items-center justify-between gap-4">
                        <button
                            onClick={() => closeMaterialsMenu()}
                            type="button"
                            className="md:hidden w-10 h-10 flex items-center justify-center p-2 bg-royalPeriwinkle rounded-full transition-theme hover:opacity-75 shrink-0"
                            aria-label="Wróć do kategorii"
                        >
                            <Icon icon="material-symbols:arrow-back-rounded" width={24} className="text-arcticMist" />
                        </button>
                        {selectedCategory && (
                            <p className="md:hidden text-accordorange h4 text-left flex-1 min-w-0 truncate">
                                {selectedCategory.label}
                            </p>
                        )}
                        <button onClick={() => closeMenus()} type='button' className='ml-auto w-10 h-10 flex items-center justify-center p-2 bg-royalPeriwinkle rounded-full transition-theme hover:opacity-75 shrink-0'>
                            <Icon icon={`ic:round-close`} width={24} className="text-arcticMist" />
                        </button>
                    </div>
                    <div className="w-full pt-28 flex flex-col items-end maxMd:items-start maxMd:pt-8">
                        <ul className="maxMd:w-full">
                            {selectedCategory && patientMaterialSections.map((section, index) => (
                                <li key={section.hash} className={index === 0 ? 'mb-4' : 'my-4'}>
                                    <Link
                                        href={`${route('front.patient.therapeuticArea', { name: selectedCategory.slug })}#${section.hash}`}
                                        title={section.label}
                                        className={menuLinkClass}
                                        onClick={() => closeMenus()}
                                    >
                                        <MenuArrowIcon />
                                        {section.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Transition>
        </>
    )
}

export default PatientNavigationMenu;
